import React, { useEffect, useState } from 'react';
import StatCard from './components/StatCard';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAppDispatch } from '../../store';
import { fetchTenantAnalytics, fetchExpensesByTenantsForPartner, fetchRevenueForLastQuater } from '../../store/actions/dashboard.action';
import { useSelector } from 'react-redux';
import NoData from '../settings/components/NoData';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface WidgetExpense {
    widgetName: string;
    expenseByWidget: number | null;
    widgetIcon: string;
}

interface WidgetAnalyticsData {
    [key: string]: string;
}

interface DataItem {
    widgetName: string;
    expenseByWidget: number;
    monthName?: string;
    previousMonthName?: string;
    monthBeforePreviousMonth?: string;
}

const PartnerDashboard = () => {
    const [limit, setLimit] = useState<number>(5);
    const [offset, setOffset] = useState<number>(0);
    const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] });
    const [analyticsdata, setAnalyticsData] = useState<any>();
    const [tenantExpensesData, setTenantExpensesData] = useState<any>([]);
    const [totalWidgetCount, setTotalWidgetCount] = useState<number | null>(null);

    const dispatch = useAppDispatch();

    const {
        userDetails,
    } = useSelector<any, any>(({ user }) => user);

    const {
        tenantAnalyticsData,
        expensesPerTenant,
        revenueLastQuater
    } = useSelector<any, any>(({ dashboard }) => dashboard);

    useEffect(() => {
        const userUuid = (userDetails && userDetails.uuid) ? userDetails.uuid : "";
        if (userUuid) {
            dispatch(fetchTenantAnalytics(userUuid));
            dispatch(fetchExpensesByTenantsForPartner(userUuid, 5, 0));
            dispatch(fetchRevenueForLastQuater(userUuid))
        }
    }, [userDetails]);

    useEffect(() => {
        const newChartData: { labels: string[], datasets: any[] } = {
            labels: [],
            datasets: []
        };
        const datasets: Record<string, Record<string, number>> = {};

        if (Array.isArray(revenueLastQuater) && revenueLastQuater.length > 0) {
            revenueLastQuater.forEach((item: any) => {
                let groupKey: string | undefined;

                if (item.monthName) {
                    groupKey = item.monthName;
                } else if (item.previousMonthName) {
                    groupKey = item.previousMonthName;
                } else if (item.monthBeforePreviousMonth) {
                    groupKey = item.monthBeforePreviousMonth;
                }

                if (groupKey) {
                    const label: any = (item.widgetName ? (item.widgetName + ' ' + groupKey) : "No data availabe for this month" + ' ' + groupKey);
                    datasets[groupKey] = datasets[groupKey] || {};
                    datasets[groupKey][label] = (item.expenseByWidget ? (datasets[groupKey][label] || 0) + item.expenseByWidget : "No data available for this month");
                }
            });

            // Generate column names since the widget names changes per each month
            const columns: string[] = [];
            Object.values(datasets).forEach((group) => {
                Object.keys(group).forEach((label) => {
                    columns.push(label);
                });
            });

            newChartData.labels = Object.keys(datasets);

            newChartData.datasets = columns.map((columnName, index) => ({
                label: columnName,
                data: Object.values(datasets).map((group) => group[columnName] || 0),
                backgroundColor: index % 3 === 0 ? '#41215a' : index % 3 === 1 ? 'rgb(247, 181, 0)' : '#343a40',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                borderWidth: 1,
                barThickness: 40,
            }));

            setChartData(newChartData);
        }
    }, [revenueLastQuater]);

    useEffect(() => {
        const titleMapping: { [key: string]: string } = {
            "tenantCount": "Total Enterprises",
            "highestRevenueTenantCount": "Highest Revenue Widgets",
            "lastMonthExpense": "Last Month Revenue Share",
            "totalWidgetCount": "Total Widget Count"
        };

        if (tenantAnalyticsData && tenantAnalyticsData !== 'null') {
            setTotalWidgetCount(tenantAnalyticsData.totalWidgetCount ? tenantAnalyticsData.totalWidgetCount : null);
            console.log("totalWidgetCount", tenantAnalyticsData.totalWidgetCount)
            const { ...otherData } = tenantAnalyticsData;
            const statCardsData = Object.entries(otherData).map(([key, amount]) => ({
                title: titleMapping[key] || key,
                amount
            }));

            setAnalyticsData(statCardsData);
        }
    }, [tenantAnalyticsData]);

    useEffect(() => {
        console.log("expensesPerTenant: ", expensesPerTenant);
       if(expensesPerTenant != undefined && Array.isArray(expensesPerTenant) && expensesPerTenant.length>0 && tenantExpensesData.length <= offset){
        let allWidgets = tenantExpensesData.concat(expensesPerTenant);
        setTenantExpensesData(allWidgets);
       }
    }, [expensesPerTenant])

    const onViewMore = () => {
        const tenantGroupId = (userDetails && userDetails.OwnedEnterprises[0]) ? userDetails.OwnedEnterprises[0].groupId : "";
        dispatch(fetchExpensesByTenantsForPartner(tenantGroupId, limit, offset + limit));
        setOffset(offset + limit);
    }

    return (
        <div className="main-content main-spacer-x-y">
            <div className="section__wrapper app-listing-section__wrapper">
                <div className="app-listing-section-title__wrapper">
                    <span>Dashboard</span>
                </div>
                <div className="db-grid-1__wrapper">
                    <div className="db-grid-inner__wrapper db-grid-2__wrapper">
                        <div className="sub-nav-inner-container stats-widgets__wrapper">
                            {analyticsdata?.map((data: any, index: any) => (
                                <StatCard key={index} title={data.title} amount={data.amount} />
                            ))}
                        </div>
                        <div className="card panel content-holder-panels">
                            <div className="panel-header">
                                <div className="panel-header-content__wrapper">
                                    <h4 className="panel-header-title">Revenue by Apps</h4>
                                    <p className="panel-header-subtitle">
                                        Your expenses by each app in the previous month
                                    </p>
                                </div>
                            </div>
                            <div className="panel-table-listing__wrapper">
                                <div className="table-parent__wrapper">
                                    <table className="table table-default">
                                        <tbody>
                                            <tr className="table-header">
                                                <th className="txt-left">App Names</th>
                                                <th className="txt-right">Total Revenue(Rs.)</th>
                                            </tr>
                                            {tenantExpensesData && tenantExpensesData.length > 0 ? (
                                                tenantExpensesData.map((widget: WidgetExpense, index: number) => (
                                                    <tr className="table-row" key={index}>
                                                        <td>
                                                            <div className="feature-details__wrapper">
                                                                <div
                                                                    className="card app-media-holder"
                                                                    style={{
                                                                        backgroundImage: `url(${widget.widgetIcon})`
                                                                    }}
                                                                ></div>
                                                                <div className="feature-details-title">
                                                                    <span className="title">{widget.widgetName}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="txt-right">{widget.expenseByWidget !== null ? widget.expenseByWidget : "N/A"}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={2}>
                                                        <h5 style={{ textAlign: 'center' }}>No Data Available</h5>
                                                    </td>
                                                </tr>
                                            )}
                                            {tenantExpensesData && tenantExpensesData.length > 0 && (
                                                <tr>
                                                    <td colSpan={2}>
                                                        <div className="panel-table-list-item">
                                                            <div className="panel-table-listing-item__inner">
                                                                <a onClick={onViewMore}>View All</a>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="card panel content-holder-panels">
                            <div className="panel-header">
                                <div className="panel-header-content__wrapper">
                                    <h4 className="panel-header-title">Revenue Overview</h4>
                                    <p className="panel-header-subtitle">
                                        Profit sharing and earning over the period of last 3 months
                                    </p>
                                </div>
                            </div>
                            <div className="panel-content">
                                {chartData.labels.length > 0 ? (
                                    <Bar data={chartData} />
                                ) : (
                                    <h5 style={{ textAlign: 'center' }}>No Data Available</h5>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PartnerDashboard;
